import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** "12s ago" / "4m ago" — short enough for a dense list. */
export function relativeTime(iso: string, now = Date.now()): string {
  const delta = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000))
  if (delta < 2) return 'now'
  if (delta < 60) return `${delta}s ago`
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`
  return `${Math.floor(delta / 86400)}d ago`
}

export function formatCoord(value: number, digits = 6): string {
  return value.toFixed(digits)
}

/**
 * What kind of thing a dot is, from the `source` the API stamped on it.
 *
 * `blueiot` and `journey` are reserved server-side — a phone cannot claim
 * either — so this is a trustworthy answer, not a guess at a free-text field.
 */
export type SourceKind = 'phone' | 'tag' | 'journey'

export function sourceKind(source: string | null | undefined): SourceKind {
  if (source === 'blueiot') return 'tag'
  if (source === 'journey') return 'journey'
  return 'phone'
}

export const SOURCE_LABEL: Record<SourceKind, string> = {
  phone: 'Phone',
  tag: 'Tag',
  journey: 'Journey',
}

/**
 * Hue band per kind, so a tag is never mistaken for a phone.
 *
 * Phones keep the whole wheel and therefore the exact colours they had before;
 * tags live in the cyans and journeys in the violets. Still hashed within the
 * band, because two tags in the same room have to be told apart too.
 */
const HUE_BAND: Record<SourceKind, { from: number; span: number; sat: number; light: number }> = {
  phone: { from: 0, span: 360, sat: 85, light: 60 },
  tag: { from: 168, span: 38, sat: 90, light: 52 },
  journey: { from: 272, span: 40, sat: 85, light: 66 },
}

/** Stable per-user hue so a device keeps its colour across reconnects. */
export function colorForUser(userId: string, source?: string | null): string {
  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = (hash << 5) - hash + userId.charCodeAt(i)
    hash |= 0
  }
  const band = HUE_BAND[sourceKind(source)]
  const hue = band.from + (Math.abs(hash) % band.span)
  return `hsl(${hue} ${band.sat}% ${band.light}%)`
}

/** A tag the API could not place on a floor is drawn on all of them. */
export function floorUnmapped(metadata: Record<string, unknown> | null | undefined): boolean {
  return metadata?.floor_unmapped === true
}

export function formatLevel(level: number | null | undefined): string {
  if (level === null || level === undefined) return '—'
  return Number.isInteger(level) ? String(level) : level.toFixed(1)
}
